// Silhouette halo for the 3D view: the discovered world floats in the void, so
// instead of the land stopping dead, rings of faint "ghost" hex cells fade outward
// from the boundary — a pale rim on the first ring traces the map's silhouette
// (after the classic hex-settlement look). Additive-blended so it reads as a soft
// glow against the dark sky. Pure view over GameState: rebuilt only when the set
// of loaded chunks changes. Owner: client-render3d.

import { BufferAttribute, BufferGeometry, DoubleSide, Mesh, MeshBasicMaterial, Scene } from 'three'
import { CHUNK, DIRS, chunkKey, hexKey, hexToPixel, tileFromIdx } from '../../shared/hex'
import { HEX_SIZE, type GameState } from '../state'
import { fogOf, fogGround } from '../render/fog'
import { CORNER } from './terrainMesh'

const S = HEX_SIZE
const RINGS = 3 // ghost-hex rings beyond the world edge
/** Cell alpha per ring — normal blending, so the ghosts read on dark night skies
 *  AND the pale day sky (an additive glow would vanish against parchment). */
const FILL = [0.10, 0.045, 0.02]
/** Rim (hex-outline band) alpha per ring — ring 1 draws the silhouette line. */
const RIM = [0.32, 0.10, 0]
const INSET = 0.94 // ghost cells shrink a touch so they read as separate tiles
const RIM_IN = 0.9 // inner edge of the outline band (thin line)
// Pale mist tint (linear r,g,b) — slightly blue so it registers on parchment too.
const TINT = { r: 0.78, g: 0.88, b: 0.98 }

interface Ghost { q: number; r: number; y: number; ring: number }

export class Halo3d {
  private mesh: Mesh | null = null
  private sig = -1
  // 4-component vertex colours (RGBA) carry per-ring alpha; normal blending.
  private mat = new MeshBasicMaterial({
    vertexColors: true, transparent: true, depthWrite: false, side: DoubleSide,
  })

  constructor(private scene: Scene) {}

  sync(st: GameState): void {
    // The halo hugs the SEEN boundary (never-seen ground renders as void), so it
    // rebuilds when the loaded set or the fog memory changes.
    const fog = fogOf(st)
    let s = st.chunks.size + (fog ? fog.version * 7919 : 0)
    for (const ck of st.chunks.values()) s = (Math.imul(s, 31) + (Math.imul(ck.cx, 73856093) ^ Math.imul(ck.cy, 19349663))) | 0
    if (s === this.sig) return
    this.sig = s
    if (this.mesh) { this.scene.remove(this.mesh); this.mesh.geometry.dispose() }
    this.mesh = this.build(st)
    if (this.mesh) this.scene.add(this.mesh)
  }

  private build(st: GameState): Mesh | null {
    const fog = fogOf(st)
    // A hex is solid ground if its chunk is loaded AND the player has seen it.
    const solid = (q: number, r: number): boolean =>
      st.chunks.has(chunkKey(Math.floor(q / CHUNK), Math.floor(r / CHUNK))) && fogGround(fog, q, r)

    // Ring 1: every void hex adjacent to solid ground. Rings 2+ expand outward.
    // All ghosts lie flat at sea level, like ripples on the void around the slab.
    const ghosts = new Map<string, Ghost>()
    let frontier: Ghost[] = []
    for (const ck of st.chunks.values()) {
      for (let i = 0; i < CHUNK * CHUNK; i++) {
        const { q, r } = tileFromIdx(ck.cx, ck.cy, i)
        if (!fogGround(fog, q, r)) continue
        for (const d of DIRS) {
          const nq = q + d.q, nr = r + d.r
          if (solid(nq, nr)) continue
          const key = hexKey(nq, nr)
          if (ghosts.has(key)) continue
          const ng = { q: nq, r: nr, y: 0, ring: 1 }
          ghosts.set(key, ng); frontier.push(ng)
        }
      }
    }
    for (let ring = 2; ring <= RINGS; ring++) {
      const next: Ghost[] = []
      for (const g of frontier) {
        for (const d of DIRS) {
          const nq = g.q + d.q, nr = g.r + d.r
          if (solid(nq, nr)) continue
          const key = hexKey(nq, nr)
          if (ghosts.has(key)) continue
          const ng = { q: nq, r: nr, y: 0, ring }
          ghosts.set(key, ng); next.push(ng)
        }
      }
      frontier = next
    }
    if (!ghosts.size) return null

    const pos: number[] = []
    const col: number[] = []
    const tri = (a: number[], b: number[], c: number[], alpha: number): void => {
      pos.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2])
      for (let i = 0; i < 3; i++) col.push(TINT.r, TINT.g, TINT.b, alpha)
    }
    for (const g of ghosts.values()) {
      const p = hexToPixel(g.q, g.r, S)
      const y = g.y + 0.25
      const fill = FILL[g.ring - 1], rim = RIM[g.ring - 1]
      for (let e = 0; e < 6; e++) {
        const c0 = CORNER[e], c1 = CORNER[(e + 1) % 6]
        if (fill > 0) {
          tri(
            [p.x, y, p.y],
            [p.x + c0.x * INSET, y, p.y + c0.z * INSET],
            [p.x + c1.x * INSET, y, p.y + c1.z * INSET],
            fill,
          )
        }
        if (rim > 0) {
          // outline band between RIM_IN and INSET along this edge
          const a = [p.x + c0.x * INSET, y, p.y + c0.z * INSET]
          const b = [p.x + c1.x * INSET, y, p.y + c1.z * INSET]
          const a2 = [p.x + c0.x * RIM_IN, y, p.y + c0.z * RIM_IN]
          const b2 = [p.x + c1.x * RIM_IN, y, p.y + c1.z * RIM_IN]
          tri(a, b, b2, rim)
          tri(a, b2, a2, rim)
        }
      }
    }
    const geo = new BufferGeometry()
    geo.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3))
    geo.setAttribute('color', new BufferAttribute(new Float32Array(col), 4)) // RGBA
    return new Mesh(geo, this.mat)
  }
}
